import { roomInviteHomePath, ROOM_INVITE_REGISTRY } from "./RoomInvite";

export const ROOM_INVITE_STORAGE_KEY = "roomInviteBridge:v1";
const TTL = 7 * 24 * 60 * 60 * 1000;
const MAX_DELIVERIES = 32;
const validId = id => typeof id === "string" && id.length > 0 && id.length <= 200 && !/[\u0000-\u001f\u007f]/.test(id);

// Only public invitation context is stored. Authentication remains in the existing account flow.
export function createRoomInviteBridge({ hallOrigin, storage, ready = () => true, onPending = () => {},
  registry = ROOM_INVITE_REGISTRY, now = () => Date.now(), sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}` }) {
  const ack = (deliveryId, status) => ({ version: 1, deliveryId, status });
  const publish = pending => {
    try { onPending(pending); } catch (_) { /* A UI subscriber cannot revoke a durable acknowledgement. */ }
  };
  const candidatePath = candidate => {
    if (typeof candidate !== "string" || !candidate || candidate.length > 4096) return null;
    try {
      const url = new URL(candidate);
      if (url.protocol !== "https:" || url.origin !== hallOrigin || url.pathname !== "/home" ||
          url.username || url.password || url.href.includes("#")) return null;
      return roomInviteHomePath(url.search, registry);
    } catch (_) { return null; }
  };
  const canonicalPath = value => typeof value === "string" && value.startsWith("/home?") &&
    candidatePath(`${hallOrigin}${value}`) === value;
  const read = () => {
    try {
      const saved = JSON.parse(storage.getItem(ROOM_INVITE_STORAGE_KEY) || "null");
      const at = now();
      const deliveries = saved && saved.version === 1 && Array.isArray(saved.deliveries) ? saved.deliveries.filter(item =>
        item && validId(item.deliveryId) && canonicalPath(item.homePath) && Number.isFinite(item.acceptedAt) &&
        item.acceptedAt <= at && at - item.acceptedAt <= TTL).slice(-MAX_DELIVERIES).map(item => ({
          deliveryId: item.deliveryId, homePath: item.homePath, acceptedAt: item.acceptedAt,
        })) : [];
      const stored = saved && saved.pending;
      const matching = stored && deliveries.find(item => item.deliveryId === stored.deliveryId && item.homePath === stored.homePath);
      const pending = matching ? {
        deliveryId: matching.deliveryId, homePath: matching.homePath,
        phase: stored.phase === "routing" && stored.routingSession === sessionId ? "routing" : "pending",
        ...(stored.phase === "routing" && stored.routingSession === sessionId ? { routingSession: sessionId } : {}),
      } : null;
      return { version: 1, deliveries, pending };
    } catch (_) { return null; }
  };
  const write = state => { try { storage.setItem(ROOM_INVITE_STORAGE_KEY, JSON.stringify(state)); return true; } catch (_) { return false; } };
  return {
    version: 1,
    receive(envelope) {
      const id = envelope && envelope.deliveryId;
      if (!envelope || envelope.version !== 1 || !validId(id)) return ack(validId(id) ? id : "", "unsupported");
      if (!ready()) return ack(id, "not_ready");
      const homePath = candidatePath(envelope.candidate);
      if (!homePath) return ack(id, "unsupported");
      const state = read();
      if (!state) return ack(id, "not_ready");
      const existing = state.deliveries.find(item => item.deliveryId === id);
      if (existing) return ack(id, existing.homePath === homePath ? "accepted" : "unsupported");
      const samePending = state.pending && state.pending.homePath === homePath;
      state.deliveries.push({ deliveryId: id, homePath, acceptedAt: now() });
      state.deliveries = state.deliveries.slice(-MAX_DELIVERIES);
      if (samePending) state.pending.deliveryId = id;
      else state.pending = { deliveryId: id, homePath, phase: "pending" };
      if (!write(state)) return ack(id, "not_ready");
      publish(state.pending);
      return ack(id, "accepted");
    },
    status(deliveryId) {
      const state = read();
      return ack(validId(deliveryId) ? deliveryId : "", validId(deliveryId) && state &&
        state.deliveries.some(item => item.deliveryId === deliveryId) ? "accepted" : "not_ready");
    },
    pending() { const state = read(); return state && state.pending; },
    markRouted(deliveryId) {
      const state = read();
      if (!state || !state.pending || state.pending.deliveryId !== deliveryId) return false;
      if (state.pending.phase === "routing") return true;
      state.pending = { ...state.pending, phase: "routing", routingSession: sessionId };
      if (!write(state)) return false;
      publish(state.pending);
      return true;
    },
    retryRoute(deliveryId) {
      const state = read();
      if (!state || !state.pending || state.pending.deliveryId !== deliveryId) return false;
      state.pending = { deliveryId, homePath: state.pending.homePath, phase: "pending" };
      return write(state);
    },
    finish(deliveryId) {
      const state = read();
      if (!state || !state.pending || state.pending.deliveryId !== deliveryId) return false;
      state.pending = null;
      if (!write(state)) return false;
      publish(null);
      return true;
    },
  };
}
